import { useMemo } from "react";
import { useGetFeatureTogglesQuery } from "../shared";
import { useSelector } from "react-redux";
import { PERMISSIONS } from "../shared";
import { selectHasPermission } from "../store/slices/authSlice";

/**
 * Custom hook to check feature toggle states and user access
 *
 * Returns feature state and access information based on:
 * - Feature state ("on" | "disabled" | "off")
 * - User permissions (VIEW_DISABLED_FEATURES)
 *
 * @param {string} featureKey - The feature to check (e.g., "newsFeed", "events")
 * @returns {Object} Feature access information
 */
export const useFeatureToggle = (featureKey) => {
  const { data: featuresData, isLoading } = useGetFeatureTogglesQuery();
  const canViewDisabled = useSelector(
    selectHasPermission(PERMISSIONS.VIEW_DISABLED_FEATURES)
  );

  const result = useMemo(() => {
    // While loading, default to hiding features for safety
    if (isLoading || !featuresData?.data) {
      return {
        state: "off",
        isOn: false,
        isDisabled: false,
        isOff: true,
        canAccess: false,
        canViewDisabled: false,
        shouldShowBanner: false,
        isLoading: true,
      };
    }

    const featureState = featuresData.data[featureKey] || "off";

    // Determine access based on state and permissions
    let canAccess = false;
    let shouldShowBanner = false;

    if (featureState === "on") {
      canAccess = true;
    } else if (featureState === "disabled") {
      canAccess = canViewDisabled;
      shouldShowBanner = canViewDisabled; // Show banner if they have access
    } else {
      // "off" - nobody can access
      canAccess = false;
    }

    return {
      state: featureState,
      isOn: featureState === "on",
      isDisabled: featureState === "disabled",
      isOff: featureState === "off",
      canAccess,
      canViewDisabled,
      shouldShowBanner,
      isLoading: false,
    };
  }, [featuresData, featureKey, canViewDisabled, isLoading]);

  return result;
};

/**
 * Hook to get all features and their access states
 *
 * Useful for navigation filtering and dashboard widget visibility
 *
 * @returns {Object} All features with access information
 */
export const useAllFeatureToggles = () => {
  const { data: featuresData, isLoading } = useGetFeatureTogglesQuery();
  const canViewDisabled = useSelector(
    selectHasPermission(PERMISSIONS.VIEW_DISABLED_FEATURES)
  );

  const result = useMemo(() => {
    if (isLoading || !featuresData?.data) {
      return {
        features: {},
        isLoading: true,
      };
    }

    const features = {};

    // Process each feature
    Object.entries(featuresData.data).forEach(([key, state]) => {
      let canAccess = false;
      let shouldShowBanner = false;

      if (state === "on") {
        canAccess = true;
      } else if (state === "disabled") {
        canAccess = canViewDisabled;
        shouldShowBanner = canViewDisabled;
      }

      features[key] = {
        state,
        isOn: state === "on",
        isDisabled: state === "disabled",
        isOff: state === "off",
        canAccess,
        canViewDisabled,
        shouldShowBanner,
      };
    });

    return {
      features,
      isLoading: false,
      canViewDisabled,
    };
  }, [featuresData, canViewDisabled, isLoading]);

  return result;
};

/**
 * Hook to check if user can view disabled features
 *
 * @returns {boolean} True if user has VIEW_DISABLED_FEATURES permission
 */
export const useCanViewDisabledFeatures = () => {
  return useSelector(selectHasPermission(PERMISSIONS.VIEW_DISABLED_FEATURES));
};
